import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Edit2, Trash2, Save, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ImageUploadButton, ImageThumbStrip } from './ImageUpload';
import { toast } from 'sonner';
import { MLText } from './MLText';
import { getBreakdownItemLabel } from '../utils/breakdownLabel';
import { formatSceneNumber } from '../utils/sceneNumber';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'PROP', label: 'Action Props', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'SCENE_PROP', label: 'Scene Props', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'COSTUME', label: 'Costume', color: 'bg-yellow-500/20 text-yellow-400' },
  { value: 'ART_SET_DRESSING', label: 'Set Dressing', color: 'bg-purple-500/20 text-purple-400' },
  { value: 'MAKEUP_HAIR', label: 'Makeup/Hair', color: 'bg-pink-500/20 text-pink-400' },
  { value: 'VFX', label: 'VFX', color: 'bg-cyan-500/20 text-cyan-400' },
  { value: 'STUNT', label: 'Stunt', color: 'bg-orange-500/20 text-orange-400' },
  { value: 'SFX_SPECIAL_EFFECTS', label: 'SFX', color: 'bg-red-500/20 text-red-400' },
  { value: 'VEHICLE', label: 'Vehicle (Prod)', color: 'bg-amber-500/20 text-amber-400' },
  { value: 'PICTURE_VEHICLE', label: 'Picture Vehicle', color: 'bg-rose-500/20 text-rose-400' },
  { value: 'ANIMAL', label: 'Animal', color: 'bg-green-500/20 text-green-400' },
  { value: 'EXTRA_BACKGROUND', label: 'Extras', color: 'bg-lime-500/20 text-lime-400' },
  { value: 'SOUND_MUSIC', label: 'Sound/Music', color: 'bg-teal-500/20 text-teal-400' },
  { value: 'PRODUCTION_NOTE', label: 'Production Note', color: 'bg-gray-500/20 text-gray-400' },
];

export const SceneEditDialog = ({ open, onClose, scene, projectId, onSaved }) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (scene) {
      setFormData({
        scene_header: scene.scene_header || '',
        synopsis: scene.synopsis || '',
        page_count_eighths: scene.page_count_eighths || 1,
        location_name: scene.location_name || '',
        location_type: scene.location_type || 'INT',
        time_of_day: scene.time_of_day || 'DAY',
        characters_in_scene: (scene.characters_in_scene || []).join(', '),
        is_flagged: scene.is_flagged || false,
        flag_reason: scene.flag_reason || ''
      });
    }
  }, [scene]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        page_count_eighths: parseInt(formData.page_count_eighths),
        characters_in_scene: formData.characters_in_scene.split(',').map(c => c.trim()).filter(Boolean)
      };
      await axios.patch(`${API}/scenes/${scene.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Scene updated');
      onSaved && onSaved();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update scene');
    } finally {
      setSaving(false);
    }
  };

  if (!scene) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0] max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['DM_Serif_Display'] text-2xl">Edit Scene {formatSceneNumber(scene.scene_number, { prefix: '#' })}</DialogTitle>
          <DialogDescription className="text-[#a0a0a0]">Update scene details and metadata</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-4 mt-4">
          <div>
            <label className="text-sm text-[#f0f0f0] mb-2 block">Scene Header</label>
            <Input
              value={formData.scene_header}
              onChange={(e) => setFormData({ ...formData, scene_header: e.target.value })}
              className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]"
              data-testid="edit-scene-header"
            />
          </div>
          <div>
            <label className="text-sm text-[#f0f0f0] mb-2 block">Synopsis</label>
            <textarea
              value={formData.synopsis}
              onChange={(e) => setFormData({ ...formData, synopsis: e.target.value })}
              className="w-full bg-[#0d0d0d] border border-[#2a2a2a] text-[#f0f0f0] rounded-lg p-3 min-h-[100px]"
              data-testid="edit-synopsis"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[#f0f0f0] mb-2 block">Location</label>
              <Input
                value={formData.location_name}
                onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]"
                data-testid="edit-location"
              />
            </div>
            <div>
              <label className="text-sm text-[#f0f0f0] mb-2 block">Page Count (eighths)</label>
              <Input
                type="number"
                min="1"
                max="80"
                value={formData.page_count_eighths}
                onChange={(e) => setFormData({ ...formData, page_count_eighths: e.target.value })}
                className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]"
                data-testid="edit-page-count"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[#f0f0f0] mb-2 block">Int/Ext</label>
              <Select value={formData.location_type} onValueChange={(v) => setFormData({...formData, location_type: v})}>
                <SelectTrigger className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]" data-testid="edit-location-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
                  <SelectItem value="INT">INT</SelectItem>
                  <SelectItem value="EXT">EXT</SelectItem>
                  <SelectItem value="INT-EXT">INT/EXT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-[#f0f0f0] mb-2 block">Time of Day</label>
              <Select value={formData.time_of_day} onValueChange={(v) => setFormData({...formData, time_of_day: v})}>
                <SelectTrigger className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]" data-testid="edit-time-of-day">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
                  <SelectItem value="DAY">Day</SelectItem>
                  <SelectItem value="NIGHT">Night</SelectItem>
                  <SelectItem value="DAWN">Dawn</SelectItem>
                  <SelectItem value="DUSK">Dusk</SelectItem>
                  <SelectItem value="CONTINUOUS">Continuous</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-sm text-[#f0f0f0] mb-2 block">Characters (comma-separated)</label>
            <Input
              value={formData.characters_in_scene}
              onChange={(e) => setFormData({ ...formData, characters_in_scene: e.target.value })}
              className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]"
              placeholder="SARAH, CAPTAIN, JOHN"
              data-testid="edit-characters"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm text-[#f0f0f0]">
              <input
                type="checkbox"
                checked={formData.is_flagged}
                onChange={(e) => setFormData({ ...formData, is_flagged: e.target.checked })}
                className="rounded"
                data-testid="edit-flagged-checkbox"
              />
              Flag this scene
            </label>
          </div>
          {formData.is_flagged && (
            <div>
              <label className="text-sm text-[#f0f0f0] mb-2 block">Flag Reason</label>
              <Input
                value={formData.flag_reason}
                onChange={(e) => setFormData({ ...formData, flag_reason: e.target.value })}
                className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]"
                placeholder="gunfire, explosion, animal, etc."
                data-testid="edit-flag-reason"
              />
            </div>
          )}
          <Button
            type="submit"
            disabled={saving}
            className="w-full bg-[#e8c547] hover:bg-[#d6b539] text-black font-medium"
            data-testid="save-scene-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const BreakdownItemEditor = ({ item, projectId, sceneId, onUpdate, onDelete, isNew = false, onCancel }) => {
  const { token } = useAuth();
  const [editing, setEditing] = useState(isNew);
  const [data, setData] = useState({
    description: item?.description || '',
    character_name: item?.character_name || '',
    notes: item?.notes || '',
    category: item?.category || 'PROP'
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!data.description.trim()) {
      toast.error('Description required');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/projects/${projectId}/breakdown-items`, {
          scene_id: sceneId,
          category: data.category,
          description: data.description,
          character_name: data.character_name || null,
          notes: data.notes || null
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Item added');
        onUpdate && onUpdate();
        onCancel && onCancel();
      } else {
        await axios.patch(`${API}/breakdown-items/${item.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Item updated');
        setEditing(false);
        onUpdate && onUpdate();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API}/breakdown-items/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Item deleted');
      onDelete && onDelete();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (editing) {
    return (
      <div className="bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg p-3 space-y-2" data-testid={isNew ? 'new-item-editor' : `edit-item-${item.id}`}>
        {isNew && (
          <Select value={data.category} onValueChange={(v) => setData({...data, category: v})}>
            <SelectTrigger className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
              {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Input
          value={data.description}
          onChange={(e) => setData({...data, description: e.target.value})}
          placeholder="Description"
          className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]"
          data-testid="item-description-input"
        />
        <Input
          value={data.character_name}
          onChange={(e) => setData({...data, character_name: e.target.value})}
          placeholder="Character (optional)"
          className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]"
        />
        <Input
          value={data.notes}
          onChange={(e) => setData({...data, notes: e.target.value})}
          placeholder="Notes (optional)"
          className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]"
        />
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#e8c547] hover:bg-[#d6b539] text-black" data-testid="save-item-btn">
            <Save className="h-3 w-3 mr-1" /> Save
          </Button>
          <Button size="sm" variant="outline" onClick={() => {
            if (isNew) onCancel && onCancel();
            else setEditing(false);
          }} className="border-[#2a2a2a]">
            <X className="h-3 w-3 mr-1" /> Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 group" data-testid={`item-${item.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 pl-4 border-l-2 border-[#2a2a2a]">
          {item.character_name && <span className="text-[#f0f0f0] font-medium">{item.character_name}: </span>}
          <MLText as="span" className="text-[#a0a0a0]">{getBreakdownItemLabel(item.description, 'original')}</MLText>
          {item.notes && <span className="text-[#4a9eff] text-sm ml-2">({item.notes})</span>}
        </div>
        <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
          <ImageUploadButton itemId={item.id} onUploaded={() => onUpdate && onUpdate()} />
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)} className="h-7 w-7 p-0 text-[#a0a0a0] hover:text-[#e8c547]" data-testid={`edit-item-btn-${item.id}`}>
            <Edit2 className="h-3 w-3" />
          </Button>
          <Button size="sm" variant="ghost" onClick={handleDelete} className="h-7 w-7 p-0 text-red-400 hover:bg-red-500/10" data-testid={`delete-item-btn-${item.id}`}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
      {item.image_paths && item.image_paths.length > 0 && (
        <div className="pl-4">
          <ImageThumbStrip paths={item.image_paths} itemId={item.id} onDelete={() => onUpdate && onUpdate()} />
        </div>
      )}
    </div>
  );
};

export { CATEGORIES };
